/**
 * WEB-24: подписка браузера на уведомления о новых сообщениях (Web Push).
 *
 * Повод (владелец, 05.09.2026): «мы не понимаем, что кому-то пришло новое
 * сообщение, и не просматриваем». Живой SSE-канал будит только ОТКРЫТУЮ
 * вкладку — человек, закрывший браузер, не узнавал ничего.
 *
 * Модуль отвечает на три вопроса и ни на один больше: можно ли здесь включить
 * уведомления, включены ли они сейчас, включить/выключить. Показ уведомления —
 * дело `public/sw.js`, отправка — дело сервера.
 *
 * 🔴 Диагноз, а не «не поддерживается». Отказы у уведомлений разные, и человеку
 * нужно разное действие:
 *  - `needs-home-screen` — iPhone в обычной вкладке Safari. У Apple Web Push
 *    работает ТОЛЬКО для сайта, добавленного на экран «Домой» (iOS ≥ 16.4);
 *    в обычной вкладке `window.PushManager` не существует вовсе. Сказать
 *    «браузер не поддерживает» здесь — соврать: поддерживает, но иначе.
 *  - `needs-safari` — iPhone, но НЕ Safari: встроенный браузер Телеграма и
 *    прочих приложений, Chrome/Яндекс на iOS. 🔴 Отдельное состояние заведено
 *    по разбору видео 08.09 (WEB-25): человеку сказали «Поделиться → На экран
 *    Домой», он честно долистал список действий до конца — а пункта там нет и
 *    быть не может, его даёт только Safari. Совет без слова «Safari» отправлял
 *    человека в тупик, и виноват был не он.
 *  - `denied` — разрешение отозвано человеком. Повторный `requestPermission()`
 *    в этом состоянии НЕ показывает окна и молча возвращает `denied`: без
 *    подсказки «разрешите в настройках сайта» тумблер выглядел бы сломанным.
 *  - `unsupported` — всё остальное (старый Android-браузер, приватное окно).
 */
import { logger } from '../utils/logger';

export const PUSH_UNSUPPORTED = 'unsupported';
export const PUSH_NEEDS_HOME_SCREEN = 'needs-home-screen';
export const PUSH_NEEDS_SAFARI = 'needs-safari';
export const PUSH_DENIED = 'denied';
export const PUSH_NOT_CONFIGURED = 'not-configured';
export const PUSH_OFF = 'off';
export const PUSH_ON = 'on';
// Браузер подписался, а сервер подписку не принял: состояние отдельное, иначе
// тумблер сказал бы «включено» о том, чего на сервере нет.
export const PUSH_FAILED = 'failed';

const SW_URL = '/sw.js';

/** iPhone/iPad, включая iPad, который представляется MacIntel с тачем. */
function isIos() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua)
    || (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1);
}

/**
 * iPhone, но НЕ Safari: встроенный браузер приложения или сторонний браузер.
 *
 * 🔴 Различать обязательно: «На экран Домой» есть только в листе «Поделиться»
 * у Safari. Совет добавить на «Домой», данный человеку во встроенном браузере
 * Телеграма, невыполним — проверено разбором записи экрана 08.09.
 *
 * На iOS все браузеры внутри — WebKit, поэтому отличаем по метке в
 * User-Agent: сторонние браузеры помечают себя явно, а встроенный WKWebView
 * (Телеграм, VK и прочие) не несёт метки `Safari/`, которая есть у настоящего
 * Safari.
 */
function isIosNonSafari() {
  if (!isIos()) return false;
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  // 🔴 Chrome на iPhone — исключение (ревью 09.09): «На экран Домой» у него в
  // своём листе «Поделиться» ЕСТЬ. Гнать его человека в Safari — лишний шаг и
  // неправда. Диагноз встроенного браузера нельзя распространять на все
  // сторонние: у остальных наличие пункта мы не проверяли, им честнее Safari.
  if (/CriOS/.test(ua)) return false;
  // Прочие сторонние браузеры на iOS помечают себя явно.
  if (/FxiOS|EdgiOS|OPT\/|YaBrowser|DuckDuckGo/.test(ua)) return true;
  // Встроенные браузеры, которые всё же помечаются.
  if (/FBAN|FBAV|Instagram|VKClient|MicroMessenger/.test(ua)) return true;
  // Остальные встроенные (Телеграм и прочие WKWebView) метки `Safari` не
  // несут вовсе. 🔴 Проверяем БЕЗ слэша: ошибиться в эту сторону дороже —
  // сказать «откройте в Safari» тому, кто уже в Safari, значит сбить с толку.
  return !/Safari/.test(ua);
}

/** Открыт как приложение с экрана «Домой», а не вкладкой. */
function isStandalone() {
  if (typeof window === 'undefined') return false;
  if (window.navigator && window.navigator.standalone === true) return true;
  return Boolean(window.matchMedia
    && window.matchMedia('(display-mode: standalone)').matches);
}

/**
 * Можно ли здесь включить уведомления. Возвращает null, если можно, иначе —
 * причину из констант выше (её и показываем человеку).
 */
export function pushBlockedReason() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return PUSH_UNSUPPORTED;
  }
  const hasAll = 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
  if (hasAll) return null;
  // 🔴 Порядок важен: на iPhone во вкладке отсутствует ровно то же самое, что
  // и в старом браузере, но лечится это добавлением на экран «Домой».
  if (isIos() && !isStandalone()) {
    // 🔴 И ещё на шаг раньше: если это не Safari, то советовать «На экран
    // Домой» бессмысленно — такого пункта в листе «Поделиться» не будет.
    // Сначала надо попасть в Safari (WEB-25, разбор видео 08.09).
    return isIosNonSafari() ? PUSH_NEEDS_SAFARI : PUSH_NEEDS_HOME_SCREEN;
  }
  return PUSH_UNSUPPORTED;
}

/** Ключ VAPID приходит base64url-строкой, а `subscribe` требует Uint8Array. */
export function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/** Только те поля, что принимает сервер: `toJSON()` даёт ещё expirationTime,
 *  а модель — extra="forbid" (иначе 422 на ровном месте). */
export function subscriptionBody(subscription) {
  const json = subscription.toJSON ? subscription.toJSON() : subscription;
  return {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  };
}

async function registration() {
  const existing = await navigator.serviceWorker.getRegistration(SW_URL);
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_URL);
}

/**
 * Состояние тумблера. Сеть не трогает — отвечает по браузеру, поэтому годится
 * для первой отрисовки экрана.
 */
export async function pushState() {
  const blocked = pushBlockedReason();
  if (blocked) return blocked;
  if (window.Notification.permission === 'denied') return PUSH_DENIED;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    if (!reg) return PUSH_OFF;
    const sub = await reg.pushManager.getSubscription();
    return sub ? PUSH_ON : PUSH_OFF;
  } catch (e) {
    logger.warn('push: не смог прочитать состояние подписки', e);
    return PUSH_OFF;
  }
}

/**
 * Включить уведомления на этом устройстве.
 * @param api экземпляр MsgApi (у участника — со своим транспортом).
 * @returns одну из констант: PUSH_ON | PUSH_DENIED | PUSH_NOT_CONFIGURED | ...
 */
export async function enablePush(api) {
  const blocked = pushBlockedReason();
  if (blocked) return blocked;

  // 🔴 Спрашиваем разрешение ПЕРВЫМ, до регистрации воркера и похода за
  // ключом: окно разрешения обязано появиться как прямой ответ на нажатие.
  // Отложи его за две сетевые операции — Safari сочтёт жест устаревшим и
  // не покажет окна вовсе.
  let permission = window.Notification.permission;
  if (permission === 'default') {
    permission = await window.Notification.requestPermission();
  }
  if (permission !== 'granted') return PUSH_DENIED;

  let key;
  try {
    const answer = await api.pushKey();
    key = answer && answer.key;
  } catch (e) {
    // 404 = уведомления на сервере не настроены (флаг выключен или нет ключей).
    // Это не поломка браузера, и говорить человеку «включите разрешение» — врать.
    logger.warn('push: сервер не отдал ключ', e);
    return PUSH_NOT_CONFIGURED;
  }
  if (!key) return PUSH_NOT_CONFIGURED;

  const reg = await registration();
  await navigator.serviceWorker.ready;
  const applicationServerKey = urlBase64ToUint8Array(key);
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    // 🔴 Подписка живёт под КОНКРЕТНЫЙ ключ сервера. Если ключ на сервере
    // сменили, повторный subscribe бросает InvalidStateError — старую надо
    // снять. Без этого тумблер молча не включался бы уже никогда.
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true, applicationServerKey,
      });
    } catch (e) {
      await sub.unsubscribe().catch(() => {});
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true, applicationServerKey,
      });
    }
  } else {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true, applicationServerKey,
    });
  }
  // 🔴 «Включено» — ТОЛЬКО после того, как сервер принял подписку (ревью,
  // Medium). Без этого отказ сервера оставлял бы подписку в браузере, а
  // `pushState()` (он смотрит только в браузер) честно отвечал бы «включено» —
  // человек читал бы «узнаете, даже если браузер закрыт», а сервер о нём не
  // знал бы вовсе. Подписку в этом случае снимаем: чужого адреса у сервиса
  // оставаться не должно.
  try {
    await api.pushSubscribe(subscriptionBody(sub));
  } catch (e) {
    logger.warn('push: сервер не принял подписку', e);
    await sub.unsubscribe().catch(() => {});
    return PUSH_FAILED;
  }
  return PUSH_ON;
}

/**
 * Пересказать серверу подписку, которая уже есть в браузере. Тихо и
 * идемпотентно — зовётся при входе в чат.
 *
 * 🔴 Зачем: ревокация устройства гасит подписку на СЕРВЕРЕ (Д-2 дубли, Д-20
 * «перенести сюда», Д-3 импорт копии), а в браузере она остаётся. Без этого
 * после переноса уведомления замолкали бы молча, а тумблер продолжал бы
 * показывать «включено».
 */
export async function resyncPush(api) {
  if (pushBlockedReason()) return;
  if (window.Notification.permission !== 'granted') return;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    const sub = reg && await reg.pushManager.getSubscription();
    if (!sub) return;
    await api.pushSubscribe(subscriptionBody(sub));
  } catch (e) {
    // Тихо: это фоновая сверка, а не действие человека.
    logger.warn('push: не удалось пересказать подписку серверу', e);
  }
}

/**
 * Выключить уведомления на этом устройстве.
 * 🔴 Сначала говорим СЕРВЕРУ: если снять подписку в браузере первым, а запрос
 * не дойдёт, сервер продолжит слать в мёртвый адрес и узнает об этом только
 * через пять отказов подряд.
 */
export async function disablePush(api) {
  try {
    await api.pushUnsubscribe();
  } catch (e) {
    logger.warn('push: сервер не принял отписку', e);
  }
  const blocked = pushBlockedReason();
  if (blocked) return blocked;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    const sub = reg && await reg.pushManager.getSubscription();
    if (sub) await sub.unsubscribe();
  } catch (e) {
    logger.warn('push: не смог снять подписку в браузере', e);
  }
  return PUSH_OFF;
}

/** Человеческий текст состояния — один на оба экрана (участник и владелец). */
export function pushHint(state) {
  switch (state) {
    case PUSH_ON:
      return 'Уведомления включены: о новом сообщении узнаете, даже если '
        + 'браузер закрыт.';
    case PUSH_OFF:
      return 'Уведомления выключены — о новых сообщениях узнаете, только '
        + 'открыв чат.';
    case PUSH_DENIED:
      return 'Уведомления запрещены в настройках браузера для этого сайта. '
        + 'Разрешите их там и нажмите ещё раз.';
    case PUSH_NEEDS_HOME_SCREEN:
      return 'На iPhone уведомления работают, только если добавить чат на '
        + 'экран «Домой»: «Поделиться» → «На экран Домой», затем откройте '
        + 'чат этим значком и включите уведомления.';
    case PUSH_NEEDS_SAFARI:
      return 'Чат открыт не в Safari, а добавить его на экран «Домой» умеет '
        + 'только Safari. Нажмите «Поделиться» → «Открыть в Safari», и уже '
        + 'там «Поделиться» → «На экран Домой». Без этого на iPhone '
        + 'уведомления не включатся.';
    case PUSH_NOT_CONFIGURED:
      return 'Уведомления на сервере пока не настроены.';
    case PUSH_FAILED:
      return 'Не удалось включить уведомления — попробуйте ещё раз.';
    default:
      return 'Этот браузер не умеет показывать уведомления.';
  }
}
